import React, { useCallback, useState, useEffect } from "react";

import {
  Intro,
  Modal,
  Text,
  Button,
  DateTimePickerPopup,
  DropdownPaginated,
} from "components/commons";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import { ConfirmModal, SuccessModal } from "components/modals";
import useDataTable from "hooks/useDataTable";
import useExport from "hooks/useExport";
import { useModal, useRouter, useApi, usePermission } from "hooks";
import locale from "localization";
import { Path, Permission, PriceBuildUpStatus } from "enums";
import { getPriceBuildUps, cancelPriceBuildUp } from "apis";
import { generateReportPricing } from "apis/generate-report.api";
import moment from "moment";

import { mapFilterToRequest, mapDataToList } from "./price-build-up.mapper";
import { columns } from "./price-build-up-column";
import { initialFilterState } from "./price-build-up-filter.state";
import PriceBuildUpFilter from "./price-build-up-filter";
import styles from "./price-build-up.module.scss";

const STATUS_OPTIONS = [
  { label: locale.active, value: PriceBuildUpStatus.Active },
  { label: locale.scheduled, value: PriceBuildUpStatus.Scheduled },
  { label: locale.expired, value: PriceBuildUpStatus.Expired },
  { label: locale.cancelled, value: PriceBuildUpStatus.Cancelled },
];

const PriceBuildUpModule = () => {
  const { history } = useRouter();
  const { can } = usePermission();
  const canCreatePbu = can(Permission.PbuCreate);
  const canEditPbu = can(Permission.PbuEdit);
  const canCancelPbu = can(Permission.PbuCancel);

  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const reasonModal = useModal();
  const [exportStatus, setExportStatus] = useState([]);
  const [exportStatusIsAll, setExportStatusIsAll] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(null);
  const [exportEndDate, setExportEndDate] = useState(null);

  const confirmModal = useModal();
  const successModal = useModal();
  const exportModal = useModal();

  const { request: requestCancel } = useApi({
    api: cancelPriceBuildUp,
    handleOwnError: true,
  });

  const { request: fetchRecentForExport, loading: isLoadingRecentForExport } = useApi({
    api: getPriceBuildUps,
    handleOwnError: true,
  });

  const { exportReport, exporting, downloadProgressModal } = useExport({
    api: generateReportPricing,
    reportType: "price-buildup",
    mappedFilterState: {
      statuses:
        exportStatusIsAll || exportStatus.length === 0
          ? undefined
          : exportStatus.map((s) => s.value).join(","),
      startDate: exportStartDate ? moment(exportStartDate).toISOString() : undefined,
      endDate: exportEndDate ? moment(exportEndDate).toISOString() : undefined,
    },
  });

  const handleView = useCallback(
    (priceBuildUp) => {
      const id = priceBuildUp.priceBuildupId ?? priceBuildUp.priceBuildUpId;
      history.push(Path.ViewPriceBuildUp, { priceBuildUpId: id });
    },
    [history],
  );

  const handleEdit = useCallback(
    (priceBuildUp) => {
      const id = priceBuildUp.priceBuildupId ?? priceBuildUp.priceBuildUpId;
      history.push(Path.EditPriceBuildUp, { priceBuildUpId: id });
    },
    [history],
  );

  const handleCancelConfirm = useCallback(
    (priceBuildUp) => {
      const id = priceBuildUp.priceBuildupId ?? priceBuildUp.priceBuildUpId;
      const status = (priceBuildUp.status || "").toUpperCase();
      if (status === "SCHEDULED") {
        confirmModal.show({ priceBuildUpId: id });
      } else {
        setCancelReason("");
        reasonModal.show({
          priceBuildUpId: id,
          customerName: priceBuildUp.station?.businessName || priceBuildUp.station?.customerName,
          status: status.charAt(0) + status.slice(1).toLowerCase(),
        });
      }
    },
    [confirmModal, reasonModal],
  );

  const { filter, search, table } = useDataTable({
    api: {
      api: getPriceBuildUps,
      mapper: (param) => param,
      params: {
        orderBy: "effectiveAt",
      },
    },
    filter: {
      initialState: { ...initialFilterState(), status: { value: ["ACTIVE"], isAll: false } },
      mapper: mapFilterToRequest,
    },
    table: {
      key: "priceBuildUps",
      mapper: (priceBuildUp) =>
        mapDataToList({
          priceBuildUp,
          canEdit: canEditPbu,
          canCancel: canCancelPbu,
          onView: handleView,
          onEdit: handleEdit,
          onCancel: handleCancelConfirm,
        }),
      columns,
    },
  });

  const handleYesCancel = useCallback(async () => {
    const { priceBuildUpId } = confirmModal;
    setIsCancelling(true);
    try {
      await requestCancel({ priceBuildUpId });
      confirmModal.close();
      successModal.show();
      table.refetch();
    } catch {
      // error handled by useApi error modal
    } finally {
      setIsCancelling(false);
    }
  }, [confirmModal, successModal, table, requestCancel]);

  const handleYesCancelWithReason = useCallback(async () => {
    if (!cancelReason.trim()) return;
    setIsCancelling(true);
    try {
      await requestCancel({ priceBuildUpId: reasonModal.priceBuildUpId, remarks: cancelReason });
      reasonModal.close();
      successModal.show();
      table.refetch();
    } catch {
      // error handled by useApi
    } finally {
      setIsCancelling(false);
    }
  }, [cancelReason, reasonModal, requestCancel, successModal, table]);

  const [pendingClear, setPendingClear] = useState(false);

  const handleClearFilter = useCallback(() => {
    filter.modifyFilter(initialFilterState());
    setPendingClear(true);
  }, [filter]);

  useEffect(() => {
    if (!pendingClear) return;
    setPendingClear(false);
    table.applyPage({ page: 1, perPage: 10 });
  }, [pendingClear]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenExport = useCallback(async () => {
    setExportStatus([{ value: PriceBuildUpStatus.Active, label: locale.active }]);
    setExportStatusIsAll(false);
    setExportStartDate(null);
    setExportEndDate(null);
    exportModal.show();
    const res = await fetchRecentForExport({
      page: 1,
      perPage: 1,
      orderBy: "effectiveAt",
      order: "desc",
      statuses: PriceBuildUpStatus.Active,
    });
    const recent = res?.priceBuildUps?.[0];
    if (recent?.effectiveAt) {
      const date = new Date(recent.effectiveAt);
      setExportStartDate(date);
      setExportEndDate(date);
    } else {
      setExportStartDate(new Date());
      setExportEndDate(new Date());
    }
  }, [exportModal, fetchRecentForExport]);

  const handleExport = useCallback(async () => {
    exportModal.close();
    await exportReport();
  }, [exportModal, exportReport]);

  return (
    <>
      <ConfirmModal
        {...downloadProgressModal}
        title="Your download is in progress"
        content="Please wait while we generate your report"
        secondary={{ text: "Got it" }}
      />
      <ConfirmModal
        {...confirmModal}
        title={locale.cancelPriceBuildUp}
        content={locale.cancelPriceBuildUpContent}
        primary={{
          text: locale.yesCancel,
          onClick: handleYesCancel,
          loading: isCancelling,
        }}
        secondary={{ text: locale.goBack }}
      />
      <ConfirmModal
        {...reasonModal}
        title={locale.cancelPriceBuildUp}
        content={
          <div>
            <div
              style={{
                color: "black",
                fontSize: 13,
                textAlign: "center",
                width: "350px",
                margin: "auto",
              }}
            >
              Are you sure you want to cancel <strong>{reasonModal.customerName || ""}</strong>{" "}
              Price Build Up?
            </div>
            <Text align={"left"} strong style={{ color: "black", marginTop: "1em" }}>
              {locale.remarks}
            </Text>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value.slice(0, 255))}
              rows={4}
              maxLength={225}
              placeholder="Type your remarks here"
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #ccc",
                borderRadius: 4,
                fontFamily: "inherit",
                fontSize: 13,
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
          </div>
        }
        primary={{
          text: locale.yesCancel,
          onClick: handleYesCancelWithReason,
          loading: isCancelling,
          disabled: !cancelReason.trim(),
        }}
        secondary={{ text: locale.goBack }}
      />
      <SuccessModal
        {...successModal}
        title={locale.success}
        content={locale.priceBuildUpSuccessfullyCancelled}
      />
      <Modal
        active={exportModal.active}
        close={exportModal.close}
        title={locale.export}
        className={styles.exportModal}
        disableEnforceFocus
      >
        <div className={styles.exportField}>
          <Text className={styles.exportLabel}>{locale.status}</Text>
          <DropdownPaginated
            isMultiple
            value={exportStatus}
            isAll={exportStatusIsAll}
            options={STATUS_OPTIONS}
            request={async () => STATUS_OPTIONS}
            formatRequest={(res) => res}
            total={STATUS_OPTIONS.length}
            optionsStyle={{ maxHeight: "unset" }}
            selectAllLabel="Select All"
            allSelectedText="All"
            placeholder="Status"
            onChange={(_, { value, isAll }) => {
              setExportStatus(value);
              setExportStatusIsAll(isAll);
            }}
            className={styles.exportDropdown}
          />
        </div>
        <div className={styles.exportField}>
          <Text className={styles.exportLabel}>Effective Start Date and Time</Text>
          <DateTimePickerPopup
            name="exportStartDate"
            value={exportStartDate}
            placeholder="MM/DD/YYYY HH:MM"
            onChange={(_, { value }) => {
              setExportStartDate(value);
              if (exportEndDate && value && value > exportEndDate) {
                setExportEndDate(value);
              }
            }}
            loading={isLoadingRecentForExport}
            className={styles.datePicker}
          />
        </div>
        <div className={styles.exportField}>
          <Text className={styles.exportLabel}>Effective End Date and Time</Text>
          <DateTimePickerPopup
            name="exportEndDate"
            value={exportEndDate}
            placeholder="MM/DD/YYYY HH:MM"
            minDateTime={exportStartDate}
            onChange={(_, { value }) => setExportEndDate(value)}
            loading={isLoadingRecentForExport}
            className={styles.datePicker}
          />
        </div>
        <div className={styles.exportActions}>
          <Button onClick={exportModal.close}>{locale.cancel}</Button>
          <Button
            primary
            loading={exporting || isLoadingRecentForExport}
            onClick={handleExport}
            data-testid="pbu-list-export-btn"
            disabled={exporting || isLoadingRecentForExport}
          >
            {locale.export}
          </Button>
        </div>
      </Modal>
      <Intro
        title={locale.priceBuildUp}
        subtitle={locale.managePriceBuildUpsForAllStations}
        {...(canCreatePbu && {
          actionText: locale.addPriceBuildUp,
          actionOnClick: () => history.push(Path.AddPriceBuildUp),
        })}
      />
      <PriceBuildUpFilter
        {...filter}
        applyClearFilter={handleClearFilter}
        {...search}
        exportReport={handleOpenExport}
        exporting={exporting}
      />
      <DataTableV2 {...table} paginationV2 horizontallyScrollable />
    </>
  );
};

export default PriceBuildUpModule;
